import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import { prepareLogsDateRange } from 'infrastructure/functions';

import type { CcmStatuses, RpmStatuses } from 'infrastructure/enums';
import type { DateRange } from 'infrastructure/types';
import type { IStatusLog } from 'infrastructure/interfaces';

const getUrl = (guid: string) => `core/patients/${guid}/combined-status-logs`;

export const COMBINED_STATUS_LOGS_PER_PAGE = 15;

interface IFilterProps {
  patientGuid: string;
  page: number;
  startTime?: number;
  endTime?: number;
}

type TBaseLog = Omit<IStatusLog, 'oldValue' | 'newValue'>;

export type TCombinedStatusLog =
  | (TBaseLog & {
      logType: 'rpm';
      oldValue: RpmStatuses | null;
      newValue: RpmStatuses;
    })
  | (TBaseLog & {
      logType: 'ccm';
      oldValue: CcmStatuses | null;
      newValue: CcmStatuses;
    });

type TResponse = {
  count: number;
  data: Array<TCombinedStatusLog>;
};

const queryFn = async ({
  patientGuid,
  page,
  startTime,
  endTime,
}: IFilterProps) => {
  const url = getUrl(patientGuid);
  const { data, error } = await dataService.getList<TResponse>(
    apiUrlGenerator(url, {
      page,
      items: COMBINED_STATUS_LOGS_PER_PAGE,
      startTime,
      endTime,
    }),
  );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

interface IProps {
  patientGuid: string;
  page: number;
  dateRange: DateRange | undefined;
}

export const useGetCombinedStatusLogs = ({
  patientGuid,
  page,
  dateRange,
}: IProps) => {
  const queryKey = ['getCombinedStatusLogs', patientGuid, page, dateRange];
  const range = prepareLogsDateRange(dateRange);

  const filter: IFilterProps = {
    patientGuid,
    page,
    ...range,
  };

  const { data, isLoading, isFetching, error } = useQuery<
    TResponse | undefined,
    string | undefined
  >({
    queryKey,
    queryFn: () => queryFn(filter),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return { data, isLoading, isFetching };
};
